/**
 * Auth Context for Olympic Fit Wellness
 * Handles authentication via Supabase
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);

  // Fetch profile function - memoized to prevent recreating
  const fetchProfile = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (data && !error) {
        setProfile(data);
      } else if (error) {
        console.error('Profile fetch error:', error);
      }
    } catch (error) {
      // Handle abort errors gracefully
      if (error.name === 'AbortError') {
        console.log('Profile fetch aborted');
      } else {
        console.error('Profile fetch error:', error);
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    let isInitializing = false;
    
    // Check initial session
    const initAuth = async () => {
      if (isInitializing) return;
      isInitializing = true;
      
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session check error:', error);
        }
        
        if (isMounted) {
          if (session?.user) {
            setUser(session.user);
            setIsAuthenticated(true);
            await fetchProfile(session.user.id);
          } else {
            setUser(null);
            setProfile(null);
            setIsAuthenticated(false);
          }
          setIsLoading(false);
          setAuthInitialized(true);
        }
      } catch (error) {
        console.error('Session init error:', error);
        if (isMounted) {
          setIsLoading(false);
          setAuthInitialized(true);
        }
      }
    };
    
    initAuth();

    // Listen for auth changes - but skip INITIAL_SESSION and first SIGNED_IN since initAuth handles it
    let hasInitialized = false;
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      
      console.log('Auth event:', event);
      
      // Skip INITIAL_SESSION - initAuth handles this
      if (event === 'INITIAL_SESSION') {
        hasInitialized = true;
        return;
      }
      
      // For SIGNED_IN on page load, skip if we haven't initialized yet (initAuth will handle it)
      if (event === 'SIGNED_IN' && !hasInitialized) {
        hasInitialized = true;
        return;
      }
      
      if (event === 'SIGNED_IN' && session?.user) {
        setUser(session.user);
        setIsAuthenticated(true);
        await fetchProfile(session.user.id);
        setIsLoading(false);
        setAuthInitialized(true);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setProfile(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        setAuthInitialized(true);
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        setUser(session.user);
        setIsAuthenticated(true);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const checkSession = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        setIsAuthenticated(true);
        await fetchProfile(session.user.id);
      }
    } catch (error) {
      console.error('Session check error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email, password, fullName) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName || email.split('@')[0]
          }
        }
      });

      if (error) throw error;
      return { success: true, data, needsVerification: !data.session };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: error.message };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        // Handle specific Supabase auth errors
        let errorMessage = error.message;
        if (error.message.includes('body stream already read')) {
          errorMessage = 'Invalid email or password. Please check your credentials.';
        }
        return { success: false, error: errorMessage };
      }
      
      if (data.user) {
        setUser(data.user);
        setIsAuthenticated(true);
        await fetchProfile(data.user.id);
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('Signin error:', error);
      // Handle the 'body stream already read' error gracefully
      if (error.message && error.message.includes('body stream already read')) {
        return { success: false, error: 'Invalid email or password. Please check your credentials.' };
      }
      return { success: false, error: error.message };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Google signin error:', error);
      return { success: false, error: error.message };
    }
  };

  const signInWithApple = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Apple signin error:', error);
      return { success: false, error: error.message };
    }
  };

  // Phone authentication methods
  const sendPhoneOTP = async (phone) => {
    try {
      const { data, error } = await supabase.auth.signInWithOtp({
        phone: phone,
        options: {
          channel: 'sms'
        }
      });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Phone OTP send error:', error);
      return { success: false, error: error.message };
    }
  };

  const verifyPhoneOTP = async (phone, token) => {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        phone: phone,
        token: token,
        type: 'sms'
      });

      if (error) throw error;
      
      if (data.user) {
        setUser(data.user);
        setIsAuthenticated(true);
        await fetchProfile(data.user.id);
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('Phone OTP verify error:', error);
      return { success: false, error: error.message };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setProfile(null);
      setIsAuthenticated(false);
      return { success: true };
    } catch (error) {
      console.error('Signout error:', error);
      return { success: false, error: error.message };
    }
  };

  const refreshProfile = async () => {
    if (user?.id) {
      await fetchProfile(user.id);
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      });

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: error.message };
    }
  };

  const updatePassword = async (currentPassword, newPassword) => {
    try {
      // Use our backend API with admin privileges
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        return { success: false, error: 'Not authenticated' };
      }
      
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ 
          current_password: currentPassword,
          new_password: newPassword 
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMsg = 'Failed to update password';
        try {
          const errorJson = JSON.parse(errorText);
          errorMsg = errorJson.detail || errorMsg;
        } catch {}
        return { success: false, error: errorMsg };
      }
      
      return { success: true };
    } catch (error) {
      console.error('Password update error:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    profile,
    isLoading,
    isAuthenticated,
    authInitialized,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithApple,
    sendPhoneOTP,
    verifyPhoneOTP,
    signOut,
    resetPassword,
    updatePassword,
    checkSession,
    refreshProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Auth Callback Component for OAuth redirects
export function AuthCallback() {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    const handleCallback = async () => {
      try {
        // Wait a moment for Supabase to process the OAuth callback
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Get the session directly - don't use context to avoid loops
        const { data: { session } } = await supabase.auth.getSession();
        
        if (isMounted) {
          if (session?.user) {
            navigate('/portal', { replace: true });
          } else {
            navigate('/login', { replace: true });
          }
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        if (isMounted) {
          navigate('/login', { replace: true });
        }
      } finally {
        if (isMounted) {
          setIsProcessing(false);
        }
      }
    };

    handleCallback();
    
    return () => {
      isMounted = false;
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-600 text-lg">Completing sign in...</p>
      </div>
    </div>
  );
}

// Protected Route Component
export function ProtectedRoute({ children, allowedRoles = null }) {
  const { profile, isLoading, isAuthenticated, authInitialized } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasRedirected, setHasRedirected] = useState(false);

  useEffect(() => {
    // Wait for auth to be fully initialized
    if (!authInitialized || isLoading) return;
    
    // Prevent multiple redirects
    if (hasRedirected) return;
    
    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      setHasRedirected(true);
      navigate('/login', { state: { from: location }, replace: true });
      return;
    }

    // Check role if specified and profile is loaded
    if (allowedRoles && profile && !allowedRoles.includes(profile.role)) {
      setHasRedirected(true);
      navigate('/portal', { replace: true });
    }
  }, [authInitialized, isLoading, isAuthenticated, allowedRoles, profile?.role, navigate, location, hasRedirected]);

  // Show loading spinner while checking auth
  if (!authInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not authenticated - will redirect in useEffect
  if (!isAuthenticated) {
    return null;
  }

  // Role not allowed - will redirect in useEffect
  if (allowedRoles && profile && !allowedRoles.includes(profile.role)) {
    return null;
  }

  return children;
}

export default AuthContext;
